<template>
  <BasePage :pageTitle="transferTitle" back-button show-menu show-footer>
    <LoadingSpinner v-if="isLoading" :text="__DICT__.loading" container-class="mt-3" />
    <template v-if="transferData">
      <!-- Bridge action: quick link to load this transfer -->
      <div class="d-flex gap-2 mb-3">
        <button class="btn btn-primary btn-sm flex-fill" @click="handleLoadClick">
          <IconScan class="icon me-1" :size="16" />
          {{ __DICT__.actionMenu.loadTransfer }}
        </button>
      </div>

      <!-- Direction Choice Modal -->
      <div class="modal modal-blur fade" :class="{ 'show': showDirectionModal }" tabindex="-1" style="display: block;" v-if="showDirectionModal">
        <div class="modal-dialog modal-sm modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ __DICT__.selectDirection }}</h5>
              <button type="button" class="btn-close" @click="showDirectionModal = false"></button>
            </div>
            <div class="modal-body d-grid gap-2">
              <button class="btn btn-primary" @click="navigateToLoad('outbound')">
                {{ __DICT__.outbound }}
              </button>
              <button class="btn btn-primary" @click="navigateToLoad('inbound')">
                {{ __DICT__.inbound }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade" :class="{ 'show': showDirectionModal }" v-if="showDirectionModal"></div>
      <!-- Transfer Details Card -->
      <Card :title-level="3" :title="__DICT__.transferDetails" show-status-bar>
        <div class="row g-3">
          <!-- Left Column -->
          <div class="col-md-6">
            <Card size="sm" margin="">
              <div class="mb-3">
                <InfoRow :label="__DICT__.route + ':'" :value="transferData.Route" />
                <InfoRow :label="__DICT__.supplier + ':'" :value="transferData.Supplier" />
                <InfoRow :label="__DICT__.capacity + ':'" :value="transferData['Seats Booked']" />
                <InfoRow :label="__DICT__.resort + ':'" :value="transferData.Resort" no-margin />
              </div>
            </Card>
          </div>

          <!-- Right Column -->
          <div class="col-md-6">
            <Card size="sm" margin="">
              <div class="mb-3">
                <InfoRow :label="__DICT__.date + ':'" :value="transferData.Date" />
                <InfoRow :label="__DICT__.status + ':'" no-margin>
                  <Badge :text="transferData.Status" :variant="getStatusVariant(transferData.Status)" />
                </InfoRow>
              </div>

              <!-- Flight Information -->
              <div v-if="transferData['Last Flight(s)']" class="mb-3">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <strong class="d-block mb-2">{{ __DICT__.lastFlights }}:</strong>
                <div class="text-muted">{{ transferData['Last Flight(s)'] }}</div>
              </div>

              <!-- Driver Information -->
              <div v-if="transferData.Driver || transferData['Driver Mobile']">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <strong class="d-block mb-2">{{ __DICT__.driverDetails }}:</strong>
                <div v-if="transferData.Driver" class="mb-1">
                  {{ transferData.Driver }}
                </div>
                <div v-if="transferData['Driver Mobile']" class="text-muted small">
                  {{ transferData['Driver Mobile'] }}
                </div>
              </div>
            </Card>

            <Card size="sm" margin="mb-3 mt-3">
              <InfoRow :label="__DICT__.capacity + ':'" :value="transferData['Seats Booked'] || 'No Capacity Listed'" />

              <TransferPassengers
                  direction="outbound"
                  :week-data="weekData"
                  :transfer-data="transferData"
                  :transfer-id="route.params.transferId"
                  :staff="outboundReps"
                  v-if="liveDataOutbound?.total > 0"
              />
              <div v-if="liveDataOutbound?.total > 0" class="ms-2 mb-3">
                <InfoRow :label="__DICT__.liveLoaded + ' (OB):'" :value="`${liveDataOutbound.loaded} / ${liveDataOutbound.total}`" />
              </div>

              <TransferPassengers
                  direction="inbound"
                  :week-data="weekData"
                  :transfer-data="transferData"
                  :transfer-id="route.params.transferId"
                  :staff="inboundReps"
                  v-if="liveDataInbound?.total > 0"
              />
              <div v-if="liveDataInbound?.total > 0" class="ms-2 mb-3">
                <InfoRow :label="__DICT__.liveLoaded + ' (IB):'" :value="`${liveDataInbound.loaded} / ${liveDataInbound.total}`" />
              </div>

              <InfoRow label="Equipment:" :value="equipmentCount" />
            </Card>
          </div>
        </div>
      </Card>


      <!-- Schedule Cards -->
      <template v-if="schedules.length > 0">
        <DepartureSchedule
            v-for="schedule in schedules"
            :key="schedule.type"
            :departures="schedule.departures"
            :title="schedule.title"
        />
      </template>
      <!-- Drop Off Schedule (reusing DepartureSchedule component) -->
      <template v-if="dropOffDepartures.length > 0">
        <DepartureSchedule
            :departures="dropOffDepartures"
            :title="'Drop Off'"
        />
      </template>

      <Card v-if="outboundReps.length > 0" size="sm" margin="mt-3 mb-3" title="Outbound Staff" showStatusBar >
        <template #actions>
          <Badge :text="outboundReps.length" />
        </template>
        <div class="list-group list-group-flush">
          <div @click="linkToRep(rep)" v-for="rep in outboundReps" :key="rep['GBS ID'] || rep.Name" class="list-group-item d-flex justify-content-between">{{ rep.Name }}<br/><span class="small">{{rep['OB Pickup Point']}}</span></div>
        </div>
      </Card>

      <Card v-if="inboundReps.length > 0" size="sm" margin="mb-3" title="Inbound Staff" showStatusBar>
        <template #actions>
          <Badge :text="inboundReps.length" />
        </template>
        <div class="list-group list-group-flush">
          <div @click="linkToRep(rep)" v-for="rep in inboundReps" :key="rep['GBS ID'] || rep.Name" class="list-group-item d-flex justify-content-between">{{ rep.Name }}<br/><span class="small">{{rep['IB Pickup Point']}}</span></div>
        </div>
      </Card>

      <!-- Passengers List -->
      <PaxAllocation
          :title="__DICT__.passengers"
          :inhabitants="passengers"
          :capacity="parseInt(transferData['Seats Booked'])"
          show-count
          show-brand
          show-equipment
      ></PaxAllocation>



    </template>

    <div v-else-if="!isLoading" class="alert alert-important alert-danger" role="alert">
      {{ __DICT__.transferNotFound }}
    </div>
  </BasePage>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useWeekStore} from '@/stores/week.js'
import BasePage from "@/components/BasePage.vue"
import PaxAllocation from "@/components/PaxAllocation.vue"
import DepartureSchedule from '@/components/DeparturesSchedule.vue'
import {api} from "@/services/api.js"
import TransferPassengers from "@/components/TransferPassengers.vue";
import { Card, InfoRow, Badge, LoadingSpinner } from "@/components/atomic";
import {IconScan} from "@tabler/icons-vue";

const route = useRoute()
const weekStore = useWeekStore()
const isLoading = ref(true)
const weekData = ref([])
const transferData = ref(null)
const repData = ref([])
const router = useRouter()
const transferDirections = ref({ outbound: false, inbound: false })
const showDirectionModal = ref(false)
const liveDataOutbound = ref(null)
const liveDataInbound = ref(null)

const transferTitle = computed(() =>
    transferData.value ? `${__DICT__.transport.transfer} ${transferData.value.transferId}` : __DICT__.transport.transfer
)

const handleLoadClick = () => {
  const hasOutbound = transferDirections.value.outbound
  const hasInbound = transferDirections.value.inbound

  if (hasOutbound && hasInbound) {
    showDirectionModal.value = true
  } else if (hasOutbound) {
    navigateToLoad('outbound')
  } else if (hasInbound) {
    navigateToLoad('inbound')
  } else {
    router.push({ name: 'selectLoadTransfer' })
  }
}

const navigateToLoad = (direction) => {
  showDirectionModal.value = false
  router.push(`/loadTransferNew/${route.params.transferId}/${direction}`)
}

const linkToRep = (rep) => {
  router.push(`/rep/${rep['GBS ID']}`)
}


const passengers = computed(() =>
    weekData.value
        .filter(passenger =>
            passenger.allocPlaces?.some(place =>
                place.transport?.transportType === "transfer" &&
                place.transport?.coach === route.params.transferId
            )
        )
        .map(passenger => {
          const place = passenger.allocPlaces?.find(place =>
              place.transport?.transportType === "transfer" &&
              place.transport?.coach === route.params.transferId
          );

          const equipmentCarriage = place?.transport?.equipmentCarriage || '';
          const hasEquipment = equipmentCarriage.startsWith('I want');

          return {
            ...passenger,
            equipment: hasEquipment
          };
        })
);


const outboundReps = computed(() => {
  return repData.value.filter(rep => rep["OB Transport"] === route.params.transferId)
})

const inboundReps = computed(() => {
  return repData.value.filter(rep => rep["IB Transport"] === route.params.transferId)
})

const equipmentCount = computed(() =>
    passengers.value.filter(passenger => passenger.equipment).length
);

const schedules = computed(() => {
  const firstPassenger = passengers.value[0]
  if (!firstPassenger?.allocPlaces) return []

  const schedules = []

  // Get both inbound and outbound transfers
  const transferAllocations = firstPassenger.allocPlaces
      .filter(place => place.transport?.transportType === "transfer" && place.transport?.coach === route.params.transferId)

  // Add schedules that have departures
  for (const allocation of transferAllocations) {
    if (allocation.transport?.departures?.length > 0) {
      schedules.push({
        type: allocation.transport.type,
        title: allocation.transport.type === 'transport-outbound'
            ? __DICT__.transport.outboundSchedule
            : __DICT__.transport.inboundSchedule,
        departures: allocation.transport.departures.sort((a, b) =>
            new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
        )
      })
    }
  }

  // Sort so outbound appears first
  return schedules.sort((a, b) =>
      a.type === 'transport-outbound' ? -1 : 1
  )
})

// Build a list of drop-off locations/times by aggregating unique
// dropOffLocation entries across passengers on this transfer
const dropOffDepartures = computed(() => {
  if (!weekData.value || weekData.value.length === 0) return []

  const itemsMap = new Map()

  for (const passenger of weekData.value) {
    const place = passenger.allocPlaces?.find(p =>
        p.transport?.transportType === 'transfer' &&
        p.transport?.coach === route.params.transferId
    )

    const loc = place?.transport?.dropOffLocation
    if (!loc) continue

    // Use location + time as a uniqueness key
    const timeKey = loc.dateTime || loc.loading_time || ''
    const key = `${loc.location || ''}|${timeKey}`
    if (!itemsMap.has(key)) {
      itemsMap.set(key, loc)
    }
  }

  const uniques = Array.from(itemsMap.values())
  uniques.sort((a, b) => {
    const at = new Date(a.dateTime || a.loading_time || 0).getTime()
    const bt = new Date(b.dateTime || b.loading_time || 0).getTime()
    return at - bt
  })
  return uniques
})

function getStatusVariant(status) {
  switch (status?.toLowerCase()) {
    case 'booked':
      return 'success'
    case 'pending':
      return 'warning'
    case 'cancelled':
      return 'danger'
    default:
      return 'secondary'
  }
}

onMounted(async () => {
  try {
    // Load both week data and transfer data in parallel
    const [weekDataResult, repDataResult, transferDataResult, allTransfersResult] = await Promise.all([
      weekStore.weekData,
      weekStore.repData,
      api.get(`/api/transfer/transferData/${weekStore.weekId}`),
      api.get(`/api/transfer/allTransfers/${weekStore.weekId}`)
    ])
    repData.value = repDataResult
    weekData.value = weekDataResult

    // Find matching transfer data
    transferData.value = transferDataResult.mergedData.find(
        transfer => transfer.transferId === route.params.transferId
    )

    transferDirections.value = {
      outbound: allTransfersResult.outbound?.includes(route.params.transferId) ?? false,
      inbound: allTransfersResult.inbound?.includes(route.params.transferId) ?? false,
    }
  } catch (error) {
    console.error('Error loading transfer data:', error)
  } finally {
    isLoading.value = false
  }

  // Fetch live ticket data for both directions after page renders
  Promise.all([
    api.get(`/api/transfer/v2/allBarcodes/${route.params.transferId}/direction/outbound`).catch(() => null),
    api.get(`/api/transfer/v2/allBarcodes/${route.params.transferId}/direction/inbound`).catch(() => null),
  ]).then(([liveOb, liveIb]) => {
    liveDataOutbound.value = liveOb
    liveDataInbound.value = liveIb
  })
})
</script>
